import React, { useState } from 'react';
import { freeToolsAPI } from '../services/api';

const MatchLite = () => {
  const [formData, setFormData] = useState({
    resumeText: '',
    jobUrlOrText: '',
    consentGiven: false
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpsellClick = async () => {
    try {
      await freeToolsAPI.trackAnalyticsEvent('match-lite_upsell_click', {
        source: 'match_lite_results',
        score: results?.score_0_100
      });
    } catch (err) {
      console.error('Failed to track upsell click:', err);
    }
    
    // Navigate to pricing page
    window.location.href = '/pricing?from=match-lite';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consentGiven) {
      setError('You must agree to the Privacy Policy to proceed.');
      return;
    }

    if (formData.resumeText.length < 50) {
      setError('Resume text must be at least 50 characters long.');
      return;
    }

    if (formData.jobUrlOrText.length < 200) {
      setError('Job description must be at least 200 characters for accurate matching.');
      return;
    }

    setLoading(true);
    setError(null);
    setRateLimited(false);
    setResults(null);

    try {
      const response = await freeToolsAPI.matchLite(
        formData.resumeText,
        formData.jobUrlOrText
      );
      
      setResults(response);
    } catch (err) {
      console.error('Match Lite analysis failed:', err);
      
      if (err.response?.status === 429) {
        setRateLimited(true);
        setError('You have reached your daily limit of 5 free analyses. Try again tomorrow or upgrade for unlimited access.');
      } else if (err.response?.status === 422) {
        setError('Could not fetch the job description from the provided URL. Please paste the job description text directly.');
      } else if (err.response?.status === 400) {
        const errorData = err.response.data;
        if (errorData.details && errorData.details.length > 0) {
          setError(errorData.details.join(', '));
        } else {
          setError('Invalid input. Please check your resume and job description.');
        }
      } else {
        setError('Analysis failed. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  const LoadingSkeleton = () => (
    <div className="mt-8 p-6 bg-white rounded-lg shadow-sm border animate-pulse">
      <div className="text-center">
        <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="h-24 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  );

  const ResultsDisplay = ({ results }) => (
    <div className="mt-8 p-6 bg-white rounded-lg shadow-sm border">
      {/* Score Display */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white text-3xl font-bold mb-4">
          {results.score_0_100}
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Match Score</h3>
        <p className="text-gray-600">{getScoreDescription(results.score_0_100)}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Missing Keywords */}
        <div className="bg-red-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">Missing Keywords</h4>
          <div className="flex flex-wrap gap-2">
            {results.missing_keywords.map((keyword, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-red-100 text-red-800 text-sm rounded-full"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>

        {/* Bullet Suggestions */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">Bullet Improvements</h4>
          <ul className="space-y-2">
            {results.bullet_suggestions.map((bullet, index) => (
              <li key={index} className="text-sm text-gray-700 flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* ATS Flags */}
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">ATS Issues</h4>
          <div className="flex items-center">
            <div className="text-2xl font-bold text-orange-600 mr-2">
              {results.ats_flags_count}
            </div>
            <div className="text-sm text-gray-600">
              {results.ats_flags_count === 0 ? 'No issues found' : 
               results.ats_flags_count === 1 ? 'Issue detected' : 
               'Issues detected'}
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-gray-500 text-center mb-6">{results.disclaimer}</p>

      {/* Upsell CTA */}
      <div className="text-center">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border-2 border-dashed border-blue-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            Want the Full Analysis?
          </h4>
          <p className="text-gray-600 mb-4">
            Get detailed recommendations, ATS compliance fixes, and a tailored cover letter.
          </p>
          <button
            onClick={handleUpsellClick}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
          >
            See Full Report
            <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  const getScoreDescription = (score) => {
    if (score >= 80) return "Excellent match! Your resume aligns well with this role.";
    if (score >= 60) return "Good match with room for improvement.";
    if (score >= 40) return "Fair match. Consider strengthening key areas.";
    return "Low match. Significant improvements needed.";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Resume Match Lite
            </h1>
            <p className="text-lg text-gray-600">
              Get a free compatibility score between your resume and any job posting
            </p>
            <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Free • No signup required
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Rate Limit Banner */}
        {rateLimited && (
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-orange-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-orange-800 font-medium">Daily limit reached</span>
            </div>
            <p className="text-orange-700 mt-1">
              You've used all 5 free analyses today. Upgrade for unlimited access or try again tomorrow.
            </p>
          </div>
        )}

        {/* Main Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Resume Input */}
            <div>
              <label htmlFor="resumeText" className="block text-sm font-medium text-gray-700 mb-2">
                Resume Text <span className="text-red-500">*</span>
              </label>
              <textarea
                id="resumeText"
                name="resumeText"
                rows={8}
                maxLength={12000}
                value={formData.resumeText}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Paste your resume text here... (minimum 50 characters)"
                required
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.resumeText.length}/12,000 characters
              </div>
            </div>

            {/* Job Description Input */}
            <div>
              <label htmlFor="jobUrlOrText" className="block text-sm font-medium text-gray-700 mb-2">
                Job Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="jobUrlOrText"
                name="jobUrlOrText"
                rows={6}
                value={formData.jobUrlOrText}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Paste job posting URL or job description text here... (minimum 200 characters)"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 You can paste either a job posting URL or the job description text directly
              </p>
            </div>

            {/* Consent Checkbox */}
            <div className="flex items-start">
              <input
                id="consentGiven"
                name="consentGiven"
                type="checkbox"
                checked={formData.consentGiven}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                required
              />
              <label htmlFor="consentGiven" className="ml-2 text-sm text-gray-700">
                I agree to the{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-500 underline"
                >
                  Privacy Policy
                </a>{' '}
                <span className="text-red-500">*</span>
              </label>
            </div>

            {/* Error Display */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex">
                  <svg className="w-5 h-5 text-red-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={loading || !formData.consentGiven}
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Analyzing...
                  </>
                ) : (
                  'Get my free score'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Loading State */}
        {loading && <LoadingSkeleton />}

        {/* Results Display */}
        {results && !loading && <ResultsDisplay results={results} />}

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Free users get 5 analyses per day. Want unlimited access? <a href="/pricing" className="text-blue-600 hover:text-blue-500 underline">Upgrade now</a></p>
        </div>
      </div>
    </div>
  );
};

export default MatchLite;